import { useAccountScope } from '../context/AccountScope';
import { useEffect, useRef, useState } from 'react';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SpeedAlertSettings() {
  const { fetch } = useAccountScope();
  const [vehicles, setVehicles] = useState([]);
  const [selection, setSelection] = useState('');
  const [limit, setLimit] = useState('80');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const vehicle = vehicles.find(item => String(item.id) === selection);

  async function load() {
    abortRef.current?.abort();
    const controller = new AbortController(); abortRef.current = controller;
    setLoading(true);
    try {
      const response = await fetch('/api/speed-alerts', { headers: headers(), signal: controller.signal });
      if (!response.ok) throw new Error('No se pudieron cargar tus activos.');
      const data = await response.json();
      if (!controller.signal.aborted) { setVehicles(data.vehicles || []); setError(''); }
    } catch (cause) {
      if (cause?.name === 'AbortError') return; if (!controller.signal.aborted) setError(cause.message); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }

  useEffect(() => {
    load(); return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save(enabled, limitKmh) {
    if (!vehicle || busy) return;
    if (!Number.isInteger(limitKmh) || limitKmh < 10 || limitKmh > 200) {
      setMessage('Escribe un límite entre 10 y 200 km/h.'); return;
    }
    setBusy(true); setMessage('');
    try {
      const response = await fetch(`/api/speed-alerts/${vehicle.id}`, {
        method: 'PUT', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, limitKmh })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'No se pudo guardar el límite.');
      setVehicles(previous => previous.map(item => item.id === vehicle.id ? { ...item, enabled, limitKmh } : item));
      setLimit(String(limitKmh)); setMessage('Ajuste de velocidad guardado.');
    } catch (cause) {
      if (cause?.name === 'AbortError') return; setMessage(cause.message); }
    finally { setBusy(false); }
  }

  return <section>
    <div className="configuration-section-heading"><span>⚡</span><div>
      <h2>Alerta de velocidad</h2><p>Define un límite para cada activo.</p>
    </div></div>
    <label className="configuration-select-label" htmlFor="speed-vehicle">Activo</label>
    <select id="speed-vehicle" value={selection} disabled={busy || loading}
      onChange={event => {
        const id = event.target.value;
        setSelection(id); setMessage('');
        setLimit(String(vehicles.find(item => String(item.id) === id)?.limitKmh || 80));
      }}>
      <option value="">Selecciona un activo</option>
      {vehicles.map(item => <option key={item.id} value={item.id}>{item.name}{item.plate ? ` · ${item.plate}` : ''}</option>)}
    </select>
    {loading && <p>Cargando…</p>}
    {error && <div className="configuration-error">{error}<button type="button" onClick={load}>Reintentar</button></div>}
    {vehicle && <>
      <div className="configuration-option">
        <div className="configuration-option-text"><strong>Avisar al superar el límite</strong>
          <small>{vehicle.enabled ? `Activo · límite ${vehicle.limitKmh} km/h` : 'Desactivado para este activo'}</small></div>
        <button type="button" role="switch" aria-label="Alerta de velocidad" aria-checked={vehicle.enabled}
          disabled={busy} className={`configuration-switch ${vehicle.enabled ? 'on' : ''}`}
          onClick={() => save(!vehicle.enabled, Number(vehicle.limitKmh))}><span /></button>
      </div>
      <label className="configuration-select-label" htmlFor="speed-limit">Límite de velocidad</label>
      <div className="configuration-speed-input">
        <input id="speed-limit" type="number" inputMode="numeric" min="10" max="200" step="1"
          value={limit} disabled={busy} onChange={event => setLimit(event.target.value)} />
        <span>km/h</span>
        <button type="button" className="configuration-action" disabled={busy || Number(limit) === Number(vehicle.limitKmh)}
          onClick={() => save(vehicle.enabled, Number(limit))}>{busy ? 'Guardando…' : 'Guardar límite'}</button>
      </div>
      <p className="configuration-hint">Recibirás un aviso por cada exceso confirmado, sin repeticiones mientras continúe.
        La alerta usa reportes nuevos del GPS; no limita la velocidad del vehículo.</p>
    </>}
    {message && <p role="status" aria-live="polite">{message}</p>}
  </section>;
}
